import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { getSchoolProfile } from '../api/schoolApi';
import toast from 'react-hot-toast';
import { FireLoader } from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { showWarning } from '../utils/alert';

const API_BASE_URL =
process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
const STATIC_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

// Helper: format rank (e.g., 1 → 1st, 2 → 2nd, 3 → 3rd, 4 → 4th)
const formatRank = (rank) => {
if (rank === null || rank === undefined || rank === '-') return '-';
const value = Number(rank);
if (!Number.isFinite(value) || value <= 0) return '-';
const lastTwo = value % 100;
if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
if (value % 10 === 1) return `${value}st`;
if (value % 10 === 2) return `${value}nd`;
if (value % 10 === 3) return `${value}rd`;
return `${value}th`;
};

// Helper: grade letter (for PDF only)
const getGradeLetter = (score) => {
if (score === null || score === undefined) return '-';
if (score >= 90) return 'A';
if (score >= 80) return 'B';
if (score >= 70) return 'C';
if (score >= 60) return 'D';
return 'F';
};

// Helper: red color style for scores below 50
const getColorStyle = (value) => {
if (value === null || value === undefined || value === '-') return 'color: black;';
const num = Number(value);
if (!isNaN(num) && num < 50) return 'color: red; font-weight: bold;';
return 'color: black;';
};

export default function StudentGradeSheet() {
const { id } = useParams();
const navigate = useNavigate();
const { user, planAccess } = useAuth();
const isStarterPlan =
String(planAccess?.plan?.name || '').trim().toLowerCase() === 'starter';
const userRole = String(user?.role || '').toLowerCase();
const canDownloadResults = ['admin', 'school_admin', 'super_admin', 'teacher'].includes(userRole);
const [loading, setLoading] = useState(true);
const [student, setStudent] = useState(null);
const [grades, setGrades] = useState([]);
const [academicYear, setAcademicYear] = useState(null);
const [resultMeta, setResultMeta] = useState({});
const [assessments, setAssessments] = useState({ affective: [], psychomotor: [] });
const [schoolProfile, setSchoolProfile] = useState(null);
const [isDownloading, setIsDownloading] = useState(false);
const [isDownloadingFront, setIsDownloadingFront] = useState(false);

useEffect(() => {
fetchGradeSheet();
}, [id]);

const fetchGradeSheet = async () => {
try {
setLoading(true);

  const [studentRes, gradesRes, schoolProfileRes] = await Promise.all([
    apiClient.get(`/students/${id}`),
    apiClient.get(`/students/${id}/grades`),
    getSchoolProfile().catch(() => null),
  ]);

  const studentData = studentRes.data.data;
  const yearData =
    gradesRes.data.academic_year ||
    studentData.academicYear ||
    studentData.academic_year ||
    null;
  const academicYearId =
    yearData?.id ||
    studentData.academic_year_id ||
    gradesRes.data.result_meta?.academic_year_id;

  let assessmentData = {
    affective: [],
    psychomotor: [],
  };

  if (academicYearId) {
    const assessmentRes = await apiClient.get(
      `/assessments/students/${id}`,
      {
        params: {
          academic_year_id: academicYearId,
        },
      },
    );

    assessmentData =
      assessmentRes.data.data || assessmentData;
  }

  setStudent(studentData);
  setGrades(gradesRes.data.data || []);
  setAcademicYear(yearData);
  setResultMeta(gradesRes.data.result_meta || {});
  setAssessments(assessmentData);
  setSchoolProfile(
    schoolProfileRes?.success
      ? schoolProfileRes.data
      : user?.school || null,
  );
} catch (error) {
  console.error('Failed to load grade sheet:', error);
  toast.error(
    error.response?.data?.message ||
      'Failed to load grade sheet',
  );
} finally {
  setLoading(false);
}

};

// --- Compute totals and averages ---
const computeTotals = () => {
const termKeys = ['term_1', 'term_2', 'term_3'];
const termTotals = { term_1: 0, term_2: 0, term_3: 0 };
let yearlyTotal = 0;
let subjectCount = grades.length;

grades.forEach(item => {
  termKeys.forEach(term => {
    const mean = item.terms[term]?.mean ?? 0;
    termTotals[term] += mean;
  });
  yearlyTotal += item.yearly.mean ?? 0;
});

const count = subjectCount || 1;
const termAverages = {
  term_1: Math.round(termTotals.term_1 / count),
  term_2: Math.round(termTotals.term_2 / count),
  term_3: Math.round(termTotals.term_3 / count)
};
const yearlyAvg = Math.round(yearlyTotal / count);

Object.keys(termTotals).forEach(key => {
  termTotals[key] = Math.round(termTotals[key]);
});
yearlyTotal = Math.round(yearlyTotal);

return { termTotals, yearlyTotal, termAverages, yearlyAvg };

};

const { termTotals, yearlyTotal, termAverages, yearlyAvg } = computeTotals();
const hasGrades = grades.length > 0;

// ==================== FRONT RESULT TEMPLATE ====================
const escapeTemplateHTML = (value) =>
String(value ?? '')
.replaceAll('&', '&amp;')
.replaceAll('<', '&lt;')
.replaceAll('>', '&gt;')
.replaceAll('"', '&quot;')
.replaceAll("'", '&#039;');

// Match the school ProfileDrawer logo handling exactly.
const getSchoolLogoPath = (data) =>
data?.logoUrl || data?.logo_url || '';

const buildSchoolLogoUrl = (path) => {
if (!path) return '';

const cleanPath = String(path).trim();
if (!cleanPath) return '';

if (/^(https?:|data:|blob:)/i.test(cleanPath)) {
  return cleanPath;
}

return `${STATIC_BASE_URL}${
  cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`
}`;

};

const getResolvedSchoolDetails = () => {
const profile = schoolProfile || {};
const authSchool = user?.school || {};

const schoolName =
  profile.name ||
  profile.school_name ||
  profile.schoolName ||
  authSchool.name ||
  authSchool.school_name ||
  authSchool.schoolName ||
  user?.school_name ||
  'School Name';

const address =
  profile.address ||
  profile.school_address ||
  profile.location ||
  authSchool.address ||
  authSchool.location ||
  '';

const city =
  profile.city ||
  profile.town ||
  authSchool.city ||
  authSchool.town ||
  '';

const state =
  profile.state ||
  profile.district ||
  authSchool.state ||
  authSchool.district ||
  '';

const country =
  profile.country ||
  authSchool.country ||
  'Sierra Leone';

const location = [address, city, state, country]
  .map((item) => String(item || '').trim())
  .filter(Boolean)
  .filter(
    (item, index, items) =>
      items.findIndex(
        (candidate) =>
          candidate.toLowerCase() === item.toLowerCase(),
      ) === index,
  )
  .join(', ');

const logoPath =
  getSchoolLogoPath(profile) ||
  getSchoolLogoPath(authSchool) ||
  profile.logo ||
  profile.school_logo ||
  profile.badge ||
  profile.badge_url ||
  profile.image ||
  profile.profile_picture ||
  authSchool.logo ||
  authSchool.badge ||
  authSchool.image ||
  '';

const motto =
  profile.motto ||
  profile.school_motto ||
  authSchool.motto ||
  '';

const resultBorderStyle =
  profile.resultBorderStyle ||
  profile.result_border_style ||
  authSchool.resultBorderStyle ||
  authSchool.result_border_style ||
  "diamond";

return {
  schoolName,
  location,
  motto,
  logoUrl: buildSchoolLogoUrl(logoPath),
  resultBorderStyle,
};

};

const generateFrontTemplateHTML = () => {
if (!student) return '';

const {
  schoolName,
  location,
  motto,
  logoUrl,
  resultBorderStyle,
} = getResolvedSchoolDetails();

const fullName = [
  student.first_name,
  student.middle_name,
  student.last_name,
]
  .filter(Boolean)
  .join(' ');

const className =
  student.class?.name ||
  student.currentClass?.name ||
  'Not Assigned';

const admissionNumber =
  student.admission_number || 'Not Assigned';

const year =
  resultMeta.academic_year ||
  academicYear?.name ||
  academicYear?.year ||
  academicYear ||
  'Not Set';

const safeSchoolName = escapeTemplateHTML(schoolName);
const safeLocation = escapeTemplateHTML(location);
const safeMotto = escapeTemplateHTML(motto);
const safeFullName = escapeTemplateHTML(fullName);
const safeClassName = escapeTemplateHTML(className);
const safeAdmissionNumber = escapeTemplateHTML(admissionNumber);
const safeYear = escapeTemplateHTML(year);
const safeLogoUrl = escapeTemplateHTML(logoUrl);
const safeBorderStyle = [
  'diamond',
  'classic',
  'royal',
  'modern',
  'traditional',
  'minimal',
  'emerald',
  'heritage',
  'executive',
  'sunburst',
  'woven',
  'prestige',
  'word_art_deco',
  'word_victorian',
  'word_flourish',
  'word_certificate',
  'word_celtic',
  'word_corner_leaf',
  'word_blue_scroll',
  'word_gold_chain',
].includes(resultBorderStyle)
  ? resultBorderStyle
  : 'diamond';

return `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>${safeSchoolName} - Front Result Template</title>
      <style>
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        @page {
          size: A4 landscape;
          margin: 6mm;
        }

        html,
        body {
          width: 100%;
          height: 100%;
          background: #fff;
        }

        body {
          font-family: Arial, Helvetica, sans-serif;
          color: #172033;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .page {
          position: relative;
          width: 100%;
          height: 198mm;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6mm;
          overflow: hidden;
        }

        .panel {
          position: relative;
          height: 100%;
          overflow: hidden;
          border: .45mm solid #1f3557;
          border-radius: 3mm;
          background: #fff;
        }

        .left-panel {
          padding: 9mm 9mm 8mm;
          background:
            linear-gradient(160deg, rgba(30, 74, 129, .06), transparent 42%),
            #fff;
        }

        .left-panel::before,
        .cover-panel::before {
          content: "";
          position: absolute;
          inset: 3mm;
          pointer-events: none;
          border: .25mm solid rgba(31, 53, 87, .38);
          border-radius: 2mm;
        }

        .key {
          margin-bottom: 5mm;
          font-size: 10px;
          font-weight: 700;
          color: #42526a;
        }

        .guide-section {
          position: relative;
          z-index: 1;
          margin-bottom: 4.3mm;
        }

        .guide-title {
          display: inline-block;
          margin-bottom: 1.6mm;
          padding-bottom: .7mm;
          border-bottom: .45mm solid #d9a629;
          color: #1f3557;
          font-size: 12px;
          line-height: 1;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .25px;
        }

        .guide-text {
          font-size: 9.2px;
          line-height: 1.48;
          color: #26364d;
        }

        .guide-text div {
          margin-bottom: .75mm;
        }

        .rating-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1mm 5mm;
        }

        .parent-note {
          position: relative;
          z-index: 1;
          margin-top: 4.5mm;
          padding: 4.2mm 5mm;
          border: .35mm solid #1f3557;
          border-radius: 2mm;
          background: #f8fafc;
        }

        .parent-note h3 {
          margin-bottom: 2mm;
          text-align: center;
          color: #1f3557;
          font-size: 10px;
          text-transform: uppercase;
          text-decoration: underline;
        }

        .parent-note p {
          font-size: 8.2px;
          line-height: 1.48;
          text-align: justify;
          color: #35445a;
        }

        .cover-panel {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 9mm 12mm 8mm;
          text-align: center;
          background:
            radial-gradient(circle at top, rgba(217, 166, 41, .12), transparent 42%),
            linear-gradient(145deg, #f8fbff 0%, #ffffff 52%, #f6f8fb 100%);
        }

        .cover-panel::after {
          content: "";
          position: absolute;
          inset: 2.2mm;
          z-index: 1;
          pointer-events: none;
          border-radius: 2.4mm;
        }

        .cover-panel.border-diamond::after {
          border: 2.4mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              45deg,
              #1f3557 0 3.2mm,
              #d9a629 3.2mm 5.4mm,
              #f7e8a7 5.4mm 7.4mm,
              #1f3557 7.4mm 10.6mm
            ) border-box;
        }

        .cover-panel.border-classic::after {
          inset: 3.2mm;
          border: 1.5mm double #1f3557;
          outline: .35mm solid #d9a629;
          outline-offset: -3.2mm;
          background: transparent;
        }

        .cover-panel.border-royal::after {
          border: 2.4mm ridge #b88a18;
          outline: .55mm solid #1f3557;
          outline-offset: -5mm;
          background: transparent;
        }

        .cover-panel.border-modern::after {
          inset: 3mm;
          border: 1.1mm solid #1f3557;
          box-shadow:
            inset 0 0 0 1.4mm #ffffff,
            inset 0 0 0 1.9mm #d9a629,
            inset 0 0 0 3mm #ffffff,
            inset 0 0 0 3.35mm rgba(31, 53, 87, .45);
          background: transparent;
        }

        .cover-panel.border-traditional::after {
          border: 2.5mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              90deg,
              #1f3557 0 4mm,
              #d9a629 4mm 6mm,
              #f7e8a7 6mm 7.3mm,
              #1f3557 7.3mm 10mm
            ) border-box;
        }

        .cover-panel.border-minimal::after {
          inset: 4mm;
          border: .65mm solid #1f3557;
          outline: .25mm solid #d9a629;
          outline-offset: -2.5mm;
          background: transparent;
        }

        .cover-panel.border-emerald::after {
          inset: 2.8mm;
          border: 2.2mm solid #14532d;
          outline: .6mm solid #d4af37;
          outline-offset: -5mm;
          box-shadow:
            inset 0 0 0 1.2mm #ffffff,
            inset 0 0 0 1.65mm rgba(20, 83, 45, .6);
          background: transparent;
        }

        .cover-panel.border-heritage::after {
          inset: 3mm;
          border: 1.4mm solid #6b4f2a;
          outline: .4mm double #c9a46c;
          outline-offset: -4.2mm;
          background: transparent;
        }

        .cover-panel.border-executive::after {
          inset: 2.4mm;
          border: 2.6mm solid #172554;
          box-shadow:
            inset 0 0 0 1mm #ffffff,
            inset 0 0 0 1.6mm #d4af37,
            inset 0 0 0 2.5mm #ffffff,
            inset 0 0 0 2.85mm rgba(23, 37, 84, .75);
          background: transparent;
        }

        .cover-panel.border-sunburst::after {
          border: 2.6mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-conic-gradient(
              from 45deg,
              #d4af37 0deg 12deg,
              #f7e8a7 12deg 24deg,
              #8a6a17 24deg 36deg
            ) border-box;
        }

        .cover-panel.border-woven::after {
          border: 2.7mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              45deg,
              #1f3557 0 3mm,
              #d9a629 3mm 4.5mm,
              #1f3557 4.5mm 7.5mm,
              #f7e8a7 7.5mm 9mm
            ) border-box;
        }

        .cover-panel.border-prestige::after {
          inset: 2.6mm;
          border: 1.2mm solid #1f3557;
          outline: .55mm solid #d9a629;
          outline-offset: -4.4mm;
          box-shadow:
            inset 0 0 0 1.4mm #ffffff,
            inset 0 0 0 1.8mm rgba(31, 53, 87, .65),
            inset 0 0 0 3.2mm #ffffff,
            inset 0 0 0 3.5mm rgba(217, 166, 41, .55);
          background: transparent;
        }

        /* Microsoft Word-inspired page-art borders. */
        .cover-panel.border-word_art_deco::after {
          border: 2.8mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              45deg,
              #172554 0 3.3mm,
              #d4af37 3.3mm 4.8mm,
              #f8fafc 4.8mm 6.2mm,
              #172554 6.2mm 9.5mm
            ) border-box;
        }

        .cover-panel.border-word_victorian::after {
          inset: 2.8mm;
          border: 1.5mm double #6b4f2a;
          outline: .55mm solid #d7b77a;
          outline-offset: -4.5mm;
          box-shadow:
            inset 0 0 0 1.2mm #ffffff,
            inset 0 0 0 1.5mm rgba(107, 79, 42, .5);
          background: transparent;
        }

        .cover-panel.border-word_flourish::after {
          inset: 3mm;
          border: 1.3mm solid #1e3a5f;
          outline: .45mm solid #c7a34b;
          outline-offset: -4.5mm;
          box-shadow:
            inset 0 0 0 1.3mm #ffffff,
            inset 0 0 0 1.65mm rgba(30, 58, 95, .45);
          background: transparent;
        }

        .cover-panel.border-word_certificate::after {
          inset: 2.4mm;
          border: 1.1mm solid #8a6a17;
          outline: .65mm double #172554;
          outline-offset: -5mm;
          box-shadow:
            inset 0 0 0 1.3mm #ffffff,
            inset 0 0 0 2mm #f3df9b,
            inset 0 0 0 3mm #ffffff,
            inset 0 0 0 3.35mm rgba(23, 37, 84, .65);
          background: transparent;
        }

        .cover-panel.border-word_celtic::after {
          border: 2.7mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              135deg,
              #14532d 0 2.8mm,
              #d4af37 2.8mm 4.2mm,
              #14532d 4.2mm 7mm,
              #f8fafc 7mm 8.2mm
            ) border-box;
        }

        .cover-panel.border-word_corner_leaf::after {
          inset: 3.5mm;
          border: .9mm solid #166534;
          outline: .3mm solid #d4af37;
          outline-offset: -3mm;
          background: transparent;
        }

        .cover-panel.border-word_blue_scroll::after {
          border: 2.8mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              90deg,
              #1e3a8a 0 4mm,
              #93c5fd 4mm 5.4mm,
              #f8fafc 5.4mm 6.8mm,
              #1e3a8a 6.8mm 10.5mm
            ) border-box;
        }

        .cover-panel.border-word_gold_chain::after {
          border: 2.7mm solid transparent;
          background:
            linear-gradient(#ffffff, #ffffff) padding-box,
            repeating-linear-gradient(
              45deg,
              #b88a18 0 2.4mm,
              #f5e7a6 2.4mm 3.8mm,
              #7c5d12 3.8mm 6mm,
              #172554 6mm 7.5mm
            ) border-box;
        }

        .border-word_victorian .cover-border-inner {
          border-color: rgba(107, 79, 42, .65);
          border-style: double;
          border-width: .6mm;
        }

        .border-word_flourish .cover-border-inner,
        .border-word_blue_scroll .cover-border-inner {
          border-color: rgba(30, 58, 95, .58);
        }

        .border-word_certificate .cover-border-inner,
        .border-word_gold_chain .cover-border-inner {
          border-color: rgba(138, 106, 23, .62);
        }

        .border-word_celtic .cover-border-inner,
        .border-word_corner_leaf .cover-border-inner {
          border-color: rgba(20, 83, 45, .58);
        }

        .border-word_art_deco .cover-corner::before,
        .border-word_art_deco .cover-corner::after,
        .border-word_certificate .cover-corner::before,
        .border-word_certificate .cover-corner::after,
        .border-word_gold_chain .cover-corner::before,
        .border-word_gold_chain .cover-corner::after {
          background: #d4af37;
        }

        .border-word_victorian .cover-corner::before,
        .border-word_victorian .cover-corner::after {
          background: #d7b77a;
        }

        .border-word_celtic .cover-corner::before,
        .border-word_celtic .cover-corner::after,
        .border-word_corner_leaf .cover-corner::before,
        .border-word_corner_leaf .cover-corner::after {
          background: #166534;
        }

        .border-word_blue_scroll .cover-corner::before,
        .border-word_blue_scroll .cover-corner::after,
        .border-word_flourish .cover-corner::before,
        .border-word_flourish .cover-corner::after {
          background: #1e3a8a;
        }

        .cover-border-inner {
          position: absolute;
          inset: 7mm;
          z-index: 1;
          pointer-events: none;
          border: .28mm solid rgba(31, 53, 87, .52);
          border-radius: 1.5mm;
        }

        .border-minimal .cover-border-inner {
          border-color: rgba(31, 53, 87, .3);
        }

        .border-classic .cover-border-inner {
          border-style: double;
          border-width: .7mm;
        }

        .cover-corner {
          position: absolute;
          z-index: 2;
          width: 8mm;
          height: 8mm;
          pointer-events: none;
        }

        .cover-corner::before,
        .cover-corner::after {
          content: "";
          position: absolute;
          background: #d9a629;
        }

        .cover-corner::before {
          width: 100%;
          height: .9mm;
          top: 0;
          left: 0;
        }

        .cover-corner::after {
          width: .9mm;
          height: 100%;
          top: 0;
          left: 0;
        }

        .cover-corner.top-left {
          top: 8mm;
          left: 8mm;
        }

        .cover-corner.top-right {
          top: 8mm;
          right: 8mm;
          transform: rotate(90deg);
        }

        .cover-corner.bottom-right {
          right: 8mm;
          bottom: 8mm;
          transform: rotate(180deg);
        }

        .cover-corner.bottom-left {
          left: 8mm;
          bottom: 8mm;
          transform: rotate(270deg);
        }

        .border-minimal .cover-corner,
        .border-modern .cover-corner,
        .border-executive .cover-corner,
        .border-prestige .cover-corner {
          display: none;
        }

        .border-emerald .cover-corner::before,
        .border-emerald .cover-corner::after {
          background: #d4af37;
        }

        .border-heritage .cover-corner::before,
        .border-heritage .cover-corner::after {
          background: #c9a46c;
        }

        .border-sunburst .cover-corner::before,
        .border-sunburst .cover-corner::after {
          background: #8a6a17;
        }

        .school-block {
          position: relative;
          z-index: 3;
          width: 100%;
        }

        .school-name {
          max-width: 100%;
          color: #1f3557;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 24px;
          line-height: 1.08;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .8px;
        }

        .school-location {
          margin-top: 2.3mm;
          color: #4c5d73;
          font-size: 10.2px;
          line-height: 1.35;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .35px;
        }

        .logo-wrap {
          width: 58mm;
          height: 40mm;
          margin: 6mm auto 4.5mm;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: visible;
          border: 0;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
        }

        .logo-wrap img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          border: 0;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
          filter: none;
        }

        .logo-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          padding: 6mm;
          color: #94a3b8;
          font-size: 8px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .motto {
          min-height: 5mm;
          color: #5a6678;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 10px;
          font-style: italic;
        }

        .report-title {
          margin-top: 7mm;
          color: #1f3557;
          font-size: 16px;
          line-height: 1.25;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .7px;
        }

        .report-subtitle {
          margin-top: 1.2mm;
          color: #d09a16;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .4px;
        }

        .student-fields {
          width: 100%;
          margin-top: 8mm;
          padding: 0 4mm;
          text-align: left;
        }

        .field-row {
          display: grid;
          grid-template-columns: 38mm 1fr;
          align-items: end;
          gap: 2mm;
          margin-bottom: 4mm;
          font-size: 10px;
          font-weight: 700;
        }

        .field-value {
          min-height: 5mm;
          padding: 0 1.5mm 1mm;
          border-bottom: .35mm dotted #1f3557;
          color: #172033;
          font-size: 10.5px;
          font-weight: 700;
        }

        .two-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6mm;
        }

        .two-fields .field-row {
          grid-template-columns: auto 1fr;
        }

        .signatures {
          position: relative;
          z-index: 2;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10mm;
          margin-top: 8mm;
          padding: 0 5mm;
        }

        .signature {
          padding-top: 5mm;
          border-top: .3mm dotted #1f3557;
          color: #44546a;
          font-size: 8.5px;
          font-weight: 700;
        }

        .brand {
          position: absolute;
          z-index: 3;
          right: 8mm;
          bottom: 8mm;
          color: rgba(31, 53, 87, .55);
          font-size: 6.7px;
          font-weight: 700;
        }

        @media print {
          html,
          body,
          .page {
            overflow: hidden !important;
          }

          .page {
            break-inside: avoid;
            page-break-inside: avoid;
            page-break-after: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="page">
        <section class="panel left-panel">
          <div class="key">Key: Mn = Average, Rnk = Position</div>

          <div class="guide-section">
            <div class="guide-title">Affective</div>
            <div class="guide-text">
              <div>1 = Class Participation, 2 = Sociability, 3 = Helpfulness, 4 = Honesty</div>
              <div>5 = Effort, 6 = Initiative, 7 = Self Discipline, 8 = Demeanour</div>
              <div>9 = Sportsmanship, 10 = Dependability / Reliability</div>
              <div>12 = School and Community Involvement, 13 = Neatness</div>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-title">Psychomotor</div>
            <div class="guide-text">
              <div>1 = Physical Dexterity, 2 = Stamina, 3 = Physical Agility</div>
              <div>4a(i) = Individual sporting prowess in ball games</div>
              <div>4a(ii) = Ability to function as a team player in ball games</div>
              <div>4b = Sporting prowess in athletics</div>
              <div>5 = Drawing / Painting skills, 6 = Handwriting</div>
              <div>7 = Physical coordination in dance</div>
              <div>8 = Voice control in talking and singing</div>
              <div>9 = Musical instrument playing skill</div>
              <div>10 = Bodily control / sense of balance</div>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-title">Cognitive — Marks / Scores</div>
            <div class="guide-text">
              <div>50% = Reasonable but needs to set goals higher</div>
              <div>Above 50% = Reasonable but must maintain effort</div>
              <div>Below 50% = Improvement needed; discuss with teacher</div>
            </div>
          </div>

          <div class="guide-section">
            <div class="guide-title">Affective and Psychomotor Ratings</div>
            <div class="guide-text rating-grid">
              <div>5 = Great / Exceptional / Always</div>
              <div>4 = Good / Often</div>
              <div>3 = Sometimes / Fair / Reasonable</div>
              <div>2 = Little / Rarely / Poor</div>
              <div>1 = Negligible / Never / Very poor</div>
            </div>
          </div>

          <div class="parent-note">
            <h3>Note to Parents / Guardian</h3>
            <p>
              This report card covers the whole pupil and not only academic
              performance. Study it carefully and discuss any area that is
              not clear with the teacher. Work closely with the school to
              help the child improve, develop good conduct, remain honest,
              disciplined, hardworking and responsible.
            </p>
          </div>
        </section>

        <section class="panel cover-panel border-${safeBorderStyle}">
          <div class="cover-border-inner"></div>
          <div class="cover-corner top-left"></div>
          <div class="cover-corner top-right"></div>
          <div class="cover-corner bottom-right"></div>
          <div class="cover-corner bottom-left"></div>
          <div class="school-block">
            <div class="school-name">${safeSchoolName}</div>
            ${
              safeLocation
                ? `<div class="school-location">${safeLocation}</div>`
                : ''
            }

            <div class="logo-wrap">
              ${
                safeLogoUrl
                  ? `<img
                      src="${safeLogoUrl}"
                      alt="${safeSchoolName} logo"
                      crossorigin="anonymous"
                      referrerpolicy="no-referrer"
                    />`
                  : `<div class="logo-placeholder">School Logo / Badge</div>`
              }
            </div>

            ${
              safeMotto
                ? `<div class="motto">“${safeMotto}”</div>`
                : `<div class="motto">&nbsp;</div>`
            }

            <div class="report-title">Continuous Assessment</div>
            <div class="report-subtitle">Report Card</div>

            <div class="student-fields">
              <div class="field-row">
                <div>Academic Year:</div>
                <div class="field-value">${safeYear}</div>
              </div>

              <div class="field-row">
                <div>Name of Pupil:</div>
                <div class="field-value">${safeFullName}</div>
              </div>

              <div class="two-fields">
                <div class="field-row">
                  <div>Admission No.:</div>
                  <div class="field-value">${safeAdmissionNumber}</div>
                </div>

                <div class="field-row">
                  <div>Class:</div>
                  <div class="field-value">${safeClassName}</div>
                </div>
              </div>
            </div>

            <div class="signatures">
              <div class="signature">Head Teacher / School Stamp</div>
              <div class="signature">Parent / Guardian</div>
            </div>
          </div>

          <div class="brand">
            Generated with PawaSchool Management System
          </div>
        </section>
      </div>
    </body>
  </html>
`;

};

const waitForPrintImages = (printWindow, timeout = 5000) => {
const images = Array.from(
printWindow.document.images || [],
);

if (!images.length) return Promise.resolve();

return Promise.race([
  Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.onload = resolve;
          image.onerror = resolve;
        }),
    ),
  ),
  new Promise((resolve) => {
    setTimeout(resolve, timeout);
  }),
]);

};

const handleDownloadFrontTemplate = async () => {
if (isStarterPlan) {
await showWarning({
title: 'PDF Download Not Available',
text: 'PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.',
});
return;
}

if (!canDownloadResults) {
toast.error(
'Only administrators and teachers can download the front result template.',
);
return;
}

setIsDownloadingFront(true);

try {
  const printWindow = window.open(
    '',
    '_blank',
    'width=1200,height=800',
  );

  if (!printWindow) {
    toast.error(
      'The print preview was blocked. Please allow pop-ups and try again.',
    );
    return;
  }

  printWindow.document.write(generateFrontTemplateHTML());
  printWindow.document.close();
  printWindow.document.title = `${
    getResolvedSchoolDetails().schoolName
  } - Front Result Template`;

  await waitForPrintImages(printWindow);

  if (printWindow.document.fonts?.ready) {
    await printWindow.document.fonts.ready;
  }

  printWindow.focus();

  setTimeout(() => {
    printWindow.print();
  }, 250);
} catch (error) {
  console.error('Front template print error:', error);
  toast.error('Failed to generate the front result template.');
} finally {
  setIsDownloadingFront(false);
}

};

// ==================== PDF GENERATION (UPDATED) ====================
const generatePrintHTML = () => {
if (!student) return '';
const fullName = `${student.first_name} ${student.last_name}`;
const className = student.class?.name || 'Not Assigned';
const year = resultMeta.academic_year || academicYear || 'Not set';
const classCount = resultMeta.class_student_count ?? student.class?.student_count ?? 0;
const studentAge = resultMeta.student_age ?? 'Not set';
const averageClassAge = resultMeta.average_class_age ?? 'Not set';
const classTeacher = resultMeta.class_teacher || 'Not assigned';

// Keep the cognitive table inside the fixed single-page print area.
// More subjects automatically produce slightly shorter rows.
const cognitiveBodyRows = Math.max(grades.length + 2, 1);
const cognitiveRowHeight = Math.max(
  3.35,
  Math.min(4.45, 89 / cognitiveBodyRows),
);

// Rankings
const positions = resultMeta.positions || student.positions || {};
const term1Rank = formatRank(positions.term_1);
const term2Rank = formatRank(positions.term_2);
const term3Rank = formatRank(positions.term_3);
const yearlyRank = formatRank(positions.yearly);

// -- Generate Cognitive rows --
let cognitiveRows = '';
grades.forEach((item) => {
  const t1 = item.terms.term_1 || {};
  const t2 = item.terms.term_2 || {};
  const t3 = item.terms.term_3 || {};
  
  cognitiveRows += `
    <tr>
      <td style="padding: .18mm .55mm; font-size: 7.7px; border: 1px solid #000; font-weight: 600; text-align: left;">${item.subject}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t1.test1)}">${t1.test1 ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t1.test2)}">${t1.test2 ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t1.mean)}">${t1.mean ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t2.test1)}">${t2.test1 ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t2.test2)}">${t2.test2 ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t2.mean)}">${t2.mean ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t3.test1)}">${t3.test1 ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t3.test2)}">${t3.test2 ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(t3.mean)}">${t3.mean ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; ${getColorStyle(item.yearly.mean)}">${item.yearly.mean ?? '-'}</td>
      <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">${formatRank(item.yearly.rank)}</td>
    </tr>
  `;
});

// Cognitive bottom rows
const cognitiveBottomRows = `
  <tr style="background: #f8f9fa;">
    <td style="padding: .18mm .55mm; font-size: 7.7px; font-weight: bold; border: 1px solid #000; text-align: left;">Combine Of All Subjects</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${termTotals.term_1 || 0}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${termTotals.term_2 || 0}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${termTotals.term_3 || 0}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${yearlyTotal}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
  </tr>
  <tr style="background: #f0f4ff;">
    <td style="padding: .18mm .55mm; font-size: 7.7px; font-weight: bold; border: 1px solid #000; text-align: left;">Average</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${termAverages.term_1 || 0}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${termAverages.term_2 || 0}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">-</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${termAverages.term_3 || 0}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000; font-weight: bold;">${yearlyAvg}</td>
    <td style="padding: .12mm .08mm; text-align: center; border: 1px solid #000;">${getGradeLetter(yearlyAvg)}</td>
  </tr>
`;

// Build the Sierra Leone affective and psychomotor result rows.
// Empty spaces are kept only when a school has fewer than 18 configured traits.
const escapeHTML = (value) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const buildAssessmentRows = (items = [], maximumRows = 18) => {
  const rows = [];

  for (let index = 0; index < maximumRows; index += 1) {
    const item = items[index];

    rows.push(`
      <tr>
        <td title="${escapeHTML(item?.name || '')}">
          ${escapeHTML(item?.code || index + 1)}
        </td>
        <td>${item?.term_1 ?? ''}</td>
        <td>${item?.term_2 ?? ''}</td>
        <td>${item?.term_3 ?? ''}</td>
        <td>${item?.yearly ?? ''}</td>
      </tr>
    `);
  }

  return rows.join('');
};

const affectiveRows = buildAssessmentRows(
  assessments.affective,
  18,
);

const psychomotorRows = buildAssessmentRows(
  assessments.psychomotor,
  18,
);

return `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8">
      <title>Student Report Card - ${fullName}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        @page {
          size: A4 landscape;
          margin: 5mm;
        }

        html,
        body {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          overflow: hidden;
          background: #fff;
        }

        body {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8.5px;
          color: #111;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .report-container {
          width: 100%;
          height: 100%;
          max-height: 200mm;
          overflow: hidden;
          padding: 3mm 1.5mm 1.5mm;
          border: .35mm solid #111;
          display: flex;
          flex-direction: column;
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .student-title {
          flex: 0 0 7mm;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 11px;
          line-height: 1.1;
          font-weight: 700;
          padding-top: .5mm;
        }

        .header-row {
          flex: 0 0 14mm;
          display: grid;
          grid-template-columns: 1fr 1.35fr 1.15fr 1.2fr;
          gap: 1.2mm 3.5mm;
          align-content: center;
          padding: .5mm .8mm 1.5mm;
          font-size: 8.5px;
          line-height: 1.1;
          font-weight: 700;
        }

        .header-item {
          min-width: 0;
          white-space: normal;
        }

        .header-item span {
          display: inline-block;
          max-width: calc(100% - 18mm);
          min-width: 15mm;
          overflow: hidden;
          text-overflow: clip;
          vertical-align: bottom;
          padding: 0 .6mm .25mm;
          border-bottom: .25mm dotted #111;
          font-weight: 600;
        }

        /* Main result area. All three tables share one exact height. */
        .main-grid {
          flex: 0 0 104mm;
          display: grid;
          grid-template-columns:
            minmax(0, 73fr)
            minmax(0, 13.5fr)
            minmax(0, 13.5fr);
          gap: .55mm;
          width: 100%;
          height: 104mm;
          min-height: 104mm;
          overflow: hidden;
          border: 0;
        }

        .col-cognitive,
        .col-affective,
        .col-psychomotor {
          min-width: 0;
          width: 100%;
          height: 104mm;
          overflow: hidden;
          border: .2mm solid #222;
          border-radius: 1.2px;
          background: #fff;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        th,
        td {
          border: .18mm solid #333;
          text-align: center;
          vertical-align: middle;
          line-height: 1.08;
        }

        .cognitive-table,
        .mini-table {
          width: 100%;
          height: 104mm;
        }

        .cognitive-table thead tr:first-child,
        .mini-table thead tr:first-child {
          height: 5mm;
        }

        .cognitive-table thead tr:nth-child(2),
        .cognitive-table thead tr:nth-child(3),
        .mini-table thead tr:nth-child(2) {
          height: 5mm;
        }

        .cognitive-table tbody tr {
          height: ${cognitiveRowHeight.toFixed(2)}mm;
        }

        .mini-table tbody tr {
          height: 5.2mm;
        }

        .section-title {
          padding: .35mm .55mm;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .1px;
          text-align: left;
          vertical-align: middle;
          background: #fff;
        }

        .mini-table .section-title {
          text-align: center;
          font-size: 8px;
        }

        /* Explicit column widths keep the subject column wide and every score box compact. */
        .cognitive-table col.subject-col { width: 29%; }
        .cognitive-table col.score-col { width: 6.4545%; }

        .cognitive-table th:first-child,
        .cognitive-table td:first-child {
          padding-left: .75mm !important;
          padding-right: .35mm !important;
          text-align: left !important;
          white-space: normal;
          overflow: hidden;
          text-overflow: clip;
          overflow-wrap: anywhere;
          word-break: normal;
          line-height: 1.04;
        }

        .cognitive-table tbody td {
          font-size: 7.3px;
        }

        .cognitive-table tbody td:first-child {
          font-size: 7.7px;
          font-weight: 600;
          letter-spacing: -.05px;
        }

        /* Compact test, mean and rank boxes without making the grid look crowded. */
        .cognitive-table th:not(:first-child),
        .cognitive-table td:not(:first-child) {
          padding-left: .06mm !important;
          padding-right: .06mm !important;
          white-space: nowrap;
        }

        .mini-table th:first-child,
        .mini-table td:first-child {
          width: 4.5mm;
        }

        .mini-table th {
          padding: .12mm .04mm;
          font-size: 5.1px;
          letter-spacing: -.12px;
          white-space: nowrap;
        }

        .mini-table td {
          padding: .06mm;
          font-size: 6.1px;
        }

        .session-section {
          flex: 0 0 14mm;
          display: grid;
          grid-template-columns: 23% 77%;
          width: 100%;
          height: 14mm;
          margin-top: 3mm;
          overflow: hidden;
          border: .25mm solid #111;
        }

        .session-left {
          display: flex;
          align-items: center;
          padding: 1mm;
          border-right: .25mm solid #111;
          font-size: 8px;
          font-weight: 700;
        }

        .session-right,
        .session-right table {
          width: 100%;
          height: 100%;
        }

        .session-right table {
          border: 0;
        }

        .session-right thead tr {
          height: 4mm;
        }

        .session-right tbody tr {
          height: 5mm;
        }

        .session-right th,
        .session-right td {
          padding: .3mm;
          font-size: 7.5px;
        }

        .signatures {
          flex: 0 0 7mm;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12mm;
          align-items: end;
          padding: 1.1mm .8mm .7mm;
          font-size: 7.5px;
        }

        .signature-field {
          display: flex;
          align-items: flex-end;
          gap: 1.5mm;
          min-width: 0;
        }

        .signature-field:last-child {
          justify-content: flex-end;
        }

        .signature-line {
          flex: 1;
          min-width: 35mm;
          height: 3mm;
          border-bottom: .2mm dotted #222;
        }

        .comments-container {
          flex: 0 0 18mm;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 3mm;
          padding: .8mm 1mm .5mm;
          overflow: hidden;
        }

        .comment-box {
          min-width: 0;
          text-align: center;
          font-size: 6.6px;
          line-height: 1.12;
        }

        .comment-heading {
          display: block;
          min-height: 4mm;
          font-weight: 700;
        }

        .comment-term {
          margin-top: .7mm;
          text-align: left;
        }

        .comment-line {
          height: 3.2mm;
          border-bottom: .2mm dotted #333;
        }

        .bottom-actions {
          flex: 0 0 10mm;
          display: grid;
          grid-template-columns: 1fr 1fr 1.15fr;
          align-items: end;
          gap: 5mm;
          padding: 1mm .8mm .8mm;
          border-top: .25mm solid #000;
          font-size: 6.6px;
          line-height: 1.15;
        }

        .bottom-field {
          min-width: 0;
        }

        .bottom-field.center {
          text-align: center;
        }

        .bottom-field.right {
          text-align: right;
        }

        .bottom-line {
          display: inline-block;
          min-width: 34mm;
          height: 2.7mm;
          margin-left: 1mm;
          border-bottom: .2mm dotted #222;
          vertical-align: bottom;
        }

        .head-comment-line {
          width: 100%;
          height: 3mm;
          margin-top: .5mm;
          border-bottom: .2mm dotted #222;
        }

        .footer-text {
          flex: 0 0 4mm;
          display: flex;
          align-items: center;
          justify-content: center;
          border-top: .2mm solid #000;
          font-size: 6.1px;
          font-weight: 700;
        }

        @media print {
          html,
          body {
            width: 100%;
            height: 100%;
            overflow: hidden !important;
          }

          .report-container {
            width: 100%;
            height: 100%;
            max-height: 200mm;
            overflow: hidden !important;
            break-after: avoid-page;
            page-break-after: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="report-container">
        <div class="student-title">${fullName} — ACADEMIC PERFORMANCE REPORT</div>
        <!-- TOP HEADER -->
        <div class="header-row">
          <div class="header-item">Class: <span>${className}</span></div>
          <div class="header-item">No. In Class: <span>${classCount}</span></div>
          <div class="header-item">Average Age of Class: <span>${averageClassAge}${typeof averageClassAge === 'number' ? ' yrs' : ''}</span></div>
          <div class="header-item">Academic Year: <span>${year}</span></div>
          <div class="header-item">Pupil's Age: <span>${studentAge}${typeof studentAge === 'number' ? ' yrs' : ''}</span></div>
          <div class="header-item">Teacher in Charge: <span>${classTeacher}</span></div>
          <div class="header-item">Term Positions: <span>${term1Rank} / ${term2Rank} / ${term3Rank}</span></div>
          <div class="header-item">Yearly Position: <span>${yearlyRank}</span></div>
        </div>

        <!-- MAIN 3 COLUMN GRID -->
        <div class="main-grid">
          <!-- COGNITIVE -->
          <div class="col-cognitive">
            <table class="cognitive-table">
              <colgroup>
                <col class="subject-col" />
                <col class="score-col" /><col class="score-col" /><col class="score-col" />
                <col class="score-col" /><col class="score-col" /><col class="score-col" />
                <col class="score-col" /><col class="score-col" /><col class="score-col" />
                <col class="score-col" /><col class="score-col" />
              </colgroup>
              <thead>
                <tr>
                  <th colspan="12" class="section-title">* COGNITIVE</th>
                </tr>
                <tr>
                  <th rowspan="2" style="padding: .18mm .55mm; font-size: 7.7px; text-align: left;">SUBJECT</th>
                  <th colspan="3" style="text-align: center; font-size: 8px;">TERM 1</th>
                  <th colspan="3" style="text-align: center; font-size: 8px;">TERM 2</th>
                  <th colspan="3" style="text-align: center; font-size: 8px;">TERM 3</th>
                  <th colspan="2" style="text-align: center; font-size: 8px;">YEARLY</th>
                </tr>
                <tr>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Test 1</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Test 2</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Mn</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Test 1</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Test 2</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Mn</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Test 1</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Test 2</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Mn</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Mn</th>
                  <th style="padding: .15mm .12mm; font-size: 6.1px;">Rk</th>
                </tr>
              </thead>
              <tbody>
                ${cognitiveRows}
                ${cognitiveBottomRows}
              </tbody>
            </table>
          </div>

          <!-- AFFECTIVE -->
          <div class="col-affective">
            <table class="mini-table">
              <colgroup>
                <col style="width:18%" />
                <col style="width:20.5%" /><col style="width:20.5%" />
                <col style="width:20.5%" /><col style="width:20.5%" />
              </colgroup>
              <thead>
                <tr>
                  <th colspan="5" class="section-title">* AFFECTIVE</th>
                </tr>
                <tr>
                  <th>No.</th><th>TERM 1</th><th>TERM 2</th><th>TERM 3</th><th>YEARLY</th>
                </tr>
              </thead>
              <tbody>
                ${affectiveRows}
              </tbody>
            </table>
          </div>

          <!-- PSYCHOMOTOR -->
          <div class="col-psychomotor">
            <table class="mini-table">
              <colgroup>
                <col style="width:18%" />
                <col style="width:20.5%" /><col style="width:20.5%" />
                <col style="width:20.5%" /><col style="width:20.5%" />
              </colgroup>
              <thead>
                <tr>
                  <th colspan="5" class="section-title">* PSYCHOMOTOR</th>
                </tr>
                <tr>
                  <th>No.</th><th>TERM 1</th><th>TERM 2</th><th>TERM 3</th><th>YEARLY</th>
                </tr>
              </thead>
              <tbody>
                ${psychomotorRows}
              </tbody>
            </table>
          </div>
        </div>

        <!-- SESSION & ATTENDANCE -->
        <div class="session-section">
          <div class="session-left">NO OF SESSION:</div>
          <div class="session-right">
            <table>
              <thead>
                <tr>
                  <th colspan="3" style="text-align: center;">TERM 1</th>
                  <th colspan="3" style="text-align: center;">TERM 2</th>
                  <th colspan="3" style="text-align: center;">TERM 3</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colspan="3">Present: ........................</td>
                  <td colspan="3">Present: ........................</td>
                  <td colspan="3">Present: ........................</td>
                </tr>
                <tr>
                  <td colspan="3">Absent: .........................</td>
                  <td colspan="3">Absent: .........................</td>
                  <td colspan="3">Absent: .........................</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- SIGNATURES -->
        <div class="signatures">
          <div class="signature-field">
            <span>Teacher's Signature &amp; Date:</span>
            <span class="signature-line"></span>
          </div>
          <div class="signature-field">
            <span>Parent's/Guardian Signature &amp; Date:</span>
            <span class="signature-line"></span>
          </div>
        </div>

        <!-- COMMENTS -->
        <div class="comments-container">
          <div class="comment-box">
            <span class="comment-heading">Guidance and Counselling / Continuous Assessment Comments</span>
            <div class="comment-term">Term: 1</div>
            <div class="comment-line"></div>
            <div class="comment-line"></div>
          </div>
          <div class="comment-box">
            <span class="comment-heading">&nbsp;</span>
            <div class="comment-term">Term: 2</div>
            <div class="comment-line"></div>
            <div class="comment-line"></div>
          </div>
          <div class="comment-box">
            <span class="comment-heading">&nbsp;</span>
            <div class="comment-term">Term: 3</div>
            <div class="comment-line"></div>
            <div class="comment-line"></div>
          </div>
        </div>

        <!-- BOTTOM ACTIONS & FOOTER -->
        <div class="bottom-actions">
          <div class="bottom-field">
            School Re-opens on <span class="bottom-line"></span>
          </div>
          <div class="bottom-field center">
            Promoted / To Repeat Class <span class="bottom-line"></span>
          </div>
          <div class="bottom-field right">
            <div>Head Teacher's end-of-year comments</div>
            <div class="head-comment-line"></div>
          </div>
        </div>

        <div class="footer-text">
          Generated with PawaSchool Management System
        </div>
      </div>
    </body>
  </html>
`;

};

const handleDownloadPDF = async () => {
if (isStarterPlan) {
await showWarning({
title: 'PDF Download Not Available',
text: 'PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.',
});
return;
}

if (!canDownloadResults) {
toast.error('Only administrators and teachers can download results.');
return;
}
if (!hasGrades) {
toast.error('No grades available to download.');
return;
}
setIsDownloading(true);
try {
const printWindow = window.open('', '_blank', 'width=1200,height=800');
if (printWindow) {
printWindow.document.write(generatePrintHTML());
printWindow.document.close();
printWindow.focus();
setTimeout(() => {
printWindow.print();
// Keep window open so user can save as PDF manually
// printWindow.close();
}, 800);
}
} catch (error) {
console.error('Print error:', error);
toast.error('Failed to generate PDF');
} finally {
setIsDownloading(false);
}
};

if (loading) return <FireLoader />;
if (!student) return <div className="p-8 text-center text-gray-500">Student not found</div>;

const termKeys = ['term_1', 'term_2', 'term_3'];
const termLabels = {
term_1: 'Term 1',
term_2: 'Term 2',
term_3: 'Term 3'
};

return (
<div className="space-y-6">
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <button
      type="button"
      onClick={() => navigate(`/app/students/${id}`)}
      className="inline-flex items-center text-gray-600 hover:text-gray-900"
    >
      <ArrowLeftIcon className="mr-2 h-5 w-5" />
      Back to Student
    </button>

    {canDownloadResults && (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleDownloadFrontTemplate}
          disabled={isDownloadingFront}
          className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          <DocumentArrowDownIcon className="mr-2 h-4 w-4" />
          {isDownloadingFront
            ? 'Preparing Front...'
            : 'Front Result Template'}
        </button>

        {hasGrades && (
          <button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
          >
            <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
            {isDownloading
              ? 'Preparing Results...'
              : 'Download Results'}
          </button>
        )}
      </div>
    )}
  </div>

  {/* WEB PREVIEW (Kept as a clean table on the UI) */}
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex flex-wrap items-center justify-between gap-2">
      <div>
        <span className="font-semibold text-gray-800">
          {student.first_name} {student.last_name}
        </span>
        <span className="text-sm text-gray-500 ml-3">
          ({student.class?.name || 'Not Assigned'})
        </span>
      </div>
      {academicYear && (
        <span className="text-sm text-blue-600 font-medium bg-blue-50 px-4 py-1.5 rounded-full border border-blue-100">
          {academicYear}
        </span>
      )}
    </div>

    <div className="overflow-x-auto p-4">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 rounded-tl-lg border border-gray-200">
              Subject
            </th>
            {termKeys.map((term) => (
              <th key={term} className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 text-center border border-gray-200" colSpan="3">
                {termLabels[term]}
              </th>
            ))}
            <th className="px-4 py-3 font-semibold text-gray-600 bg-gray-50/50 text-center border border-gray-200" colSpan="2">
              Yearly
            </th>
          </tr>
          <tr className="border-b border-gray-100">
            <th className="px-4 py-2 text-xs text-gray-400 font-medium border border-gray-200"></th>
            {termKeys.map((term) => (
              <React.Fragment key={term}>
                <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Test1</th>
                <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Test2</th>
                <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Mean</th>
              </React.Fragment>
            ))}
            <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Mean</th>
            <th className="px-2 py-2 text-xs text-gray-400 font-medium text-center border border-gray-200">Rank</th>
          </tr>
        </thead>
        <tbody>
          {grades.map((item, index) => (
            <tr key={index} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
              <td className="px-4 py-3 font-medium text-gray-800 border border-gray-100">{item.subject}</td>
              {termKeys.map((term) => {
                const termData = item.terms[term] || {};
                return (
                  <React.Fragment key={term}>
                    <td className={`px-2 py-3 text-center border border-gray-100 ${Number(termData.test1) < 50 ? 'text-red-500 font-bold' : ''}`}>
                      {termData.test1 ?? '-'}
                    </td>
                    <td className={`px-2 py-3 text-center border border-gray-100 ${Number(termData.test2) < 50 ? 'text-red-500 font-bold' : ''}`}>
                      {termData.test2 ?? '-'}
                    </td>
                    <td className={`px-2 py-3 text-center font-semibold border border-gray-100 ${Number(termData.mean) < 50 ? 'text-red-500 font-bold' : ''}`}>
                      {termData.mean ?? '-'}
                    </td>
                  </React.Fragment>
                );
              })}
              <td className={`px-2 py-3 text-center font-bold border border-gray-100 ${Number(item.yearly.mean) < 50 ? 'text-red-500 font-bold' : ''}`}>
                {item.yearly.mean ?? '-'}
              </td>
              <td className="px-2 py-3 text-center text-sm text-gray-500 border border-gray-100">{formatRank(item.yearly.rank)}</td>
            </tr>
          ))}
          {hasGrades && (
            <>
              <tr className="bg-gray-50/70 border-t border-gray-200">
                <td className="px-4 py-3 font-semibold text-gray-700 border border-gray-100">Combine of all Subjects</td>
                {termKeys.map((term) => (
                  <React.Fragment key={term}>
                    <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                    <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                    <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">{termTotals[term] || 0}</td>
                  </React.Fragment>
                ))}
                <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">{yearlyTotal}</td>
                <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
              </tr>
              <tr className="bg-gray-50/50 border-t border-gray-200">
                <td className="px-4 py-3 font-semibold text-gray-700 border border-gray-100">Average</td>
                {termKeys.map((term) => (
                  <React.Fragment key={term}>
                    <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                    <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
                    <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">{termAverages[term] || 0}</td>
                  </React.Fragment>
                ))}
                <td className="px-2 py-3 text-center font-semibold text-gray-800 border border-gray-100">{yearlyAvg}</td>
                <td className="px-2 py-3 text-center text-gray-500 border border-gray-100">-</td>
              </tr>
            </>
          )}
          {grades.length === 0 && (
            <tr>
              <td colSpan={4 + termKeys.length * 3 + 2} className="px-6 py-8 text-center text-gray-500 border-t border-gray-100">
                No grades recorded yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="px-6 py-3 bg-gray-50/50 text-xs text-gray-400 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
      <span className="font-medium text-gray-500">Mean = average of Test1 and Test2</span>
      <span>Rank based on class performance</span>
    </div>
  </div>
</div>

);
}